import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api, API_URL, resolveMediaUrl } from '../utils/api';
import Skeleton from '../components/Skeleton';
const Logo3D = lazy(() => import('../components/Logo3D'));

/* ─── Services data ──────────────────────────────────────────────────────── */
const SERVICES = [
  { icon: '✂️', name: 'Haircuts', desc: 'Precision cuts tailored to your style and face shape.', price: 'EGP 30' },
  { icon: '🪒', name: 'Shaves', desc: 'Classic hot towel shave for the smoothest finish.', price: 'EGP 20' },
  { icon: '🧔', name: 'Beard Trims', desc: 'Shape and define your beard to perfection.', price: 'EGP 15' },
  { icon: '💆', name: 'Facial Treatments', desc: 'Rejuvenate your skin with our premium facial care.', price: 'EGP 40' },
  { icon: '💈', name: 'Hair Styling', desc: 'Expert styling using top-tier products for any occasion.', price: 'EGP 25' },
  { icon: '🧴', name: 'Scalp Treatments', desc: 'Deep conditioning treatments for a healthy scalp.', price: 'EGP 35' },
];

/* ─── Products data ──────────────────────────────────────────────────────── */
const PRODUCTS = [
  { icon: '🪮', name: 'Shaving Brush', desc: 'Premium badger hair brush' },
  { icon: '✂️', name: 'Barber Scissors', desc: 'Professional grade steel' },
  { icon: '🔋', name: 'Electric Trimmer', desc: 'Cordless precision trimmer' },
  { icon: '🧴', name: 'Pomade', desc: 'Strong hold, natural shine' },
  { icon: '🪒', name: 'Safety Razor', desc: 'Classic double-edge razor' },
  { icon: '💈', name: 'Styling Wax', desc: 'Matte finish, flexible hold' },
];

/* ─── Blog posts ─────────────────────────────────────────────────────────── */
const BLOG_POSTS = [
  { title: 'The Ultimate Guide to Fade Haircuts', date: 'Jan 15, 2025', excerpt: 'Looking for a new cut? Check out our guide to the most popular fade styles for every face shape.' },
  { title: 'Top Haircuts for 2025', date: 'Jan 22, 2025', excerpt: 'Discover the trending cuts that are dominating barbershops this year and find your perfect style.' },
  { title: 'Benefits of Regular Scalp Treatments', date: 'Feb 3, 2025', excerpt: 'Learn how regular scalp treatments can transform the health and appearance of your hair.' },
];

const GENDER_CATEGORIES = { male: ['men'], female: ['women', 'nails'] };

/* ─── Gallery items (before/after pairs) ────────────────────────────────── */
const GALLERY = [
  { before: '👨‍🦱', after: '💈', label: 'Classic Fade' },
  { before: '🧔', after: '😎', label: 'Beard Sculpt' },
  { before: '👨‍🦳', after: '✨', label: 'Full Makeover' },
  { before: '👦', after: '💈', label: 'Kids Cut' },
];

/* ─── Section Header ─────────────────────────────────────────────────────── */
function SectionHeader({ label, title, subtitle, titleColor }) {
  const textStyle = titleColor ? { color: titleColor } : undefined;
  return (
    <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
      {label && <p className="section-label" style={textStyle}>{label}</p>}
      <h2 className="section-title" style={textStyle}>{title}</h2>
      <div className="orange-bar-sm" style={{ display: 'block', margin: '0.75rem auto' }} />
      {subtitle && <p style={{ color: titleColor || 'var(--text-muted)', fontSize: '0.9rem', maxWidth: '500px', margin: '0.75rem auto 0', lineHeight: 1.7 }}>{subtitle}</p>}
    </div>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { signupTheme } = useTheme();

  const [topShops, setTopShops] = useState([]);
  const [topBranches, setTopBranches] = useState([]);
  const [topArtists, setTopArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productIdx, setProductIdx] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const fetchTopRated = async () => {
      try {
        const artistLimit = user?.gender ? 18 : 6;
        const [shops, branches, artists] = await Promise.all([
          api.getTopRatedShops(6),
          api.getTopRatedBranches(6),
          api.getTopRatedArtists(artistLimit),
        ]);
        setTopShops(shops);
        setTopBranches(branches);
        setTopArtists(artists);
      } catch (err) {
        console.error('Failed to fetch top-rated items:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTopRated();
  }, [user?.gender]);

  const useFemaleTheme = user ? user.gender === 'female' : signupTheme === 'female';
  const accentColor = useFemaleTheme ? '#ff00aa' : '#0064b2';
  const accentLight = useFemaleTheme ? '#ff4dc4' : '#3399e6';
  const accentDark = useFemaleTheme ? '#cc0088' : '#004d8c';
  const sparkleSecondary = useFemaleTheme ? '#ff99dd' : '#3399e6';

  const formatRating = (rating) => {
    if (!rating || rating === 0) return t('common.noRatingsYet');
    const n = typeof rating === 'number' ? rating : parseFloat(rating);
    return isNaN(n) ? t('common.noRatingsYet') : `${n.toFixed(1)} ★`;
  };

  const visibleProducts = PRODUCTS.slice(productIdx, productIdx + 3);

  const displayTopShops = user?.gender
    ? topShops.filter(s => GENDER_CATEGORIES[user.gender]?.includes(s.category))
    : topShops;

  const displayTopArtists = user?.gender
    ? topArtists.filter(a => GENDER_CATEGORIES[user.gender]?.includes(a.shop_category)).slice(0, 6)
    : topArtists;

  /* ─── Shared section wrapper style ── */
  const sectionStyle = (alt = false) => ({
    padding: '5rem 1.5rem',
    background: alt ? 'var(--bg-dark)' : 'var(--bg-base)',
    position: 'relative',
  });

  /* ─────────────────────────────────────────────────────────────────────── */
  return (
    <div style={{ fontFamily: "'Roboto', sans-serif", color: '#ffffff', background: 'var(--bg-base)' }}>

      {/* ════════════════════════════════════════════════════════════════════
          HERO
      ════════════════════════════════════════════════════════════════════ */}
      <section style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', overflow: 'hidden', background: 'var(--bg-base)' }}>

        {/* Large faded text watermark */}
        <div style={{ position: 'absolute', bottom: '5%', left: '50%', transform: 'translateX(-50%)', fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(5rem, 18vw, 14rem)', letterSpacing: '0.08em', color: 'var(--orange-glow)', whiteSpace: 'nowrap', pointerEvents: 'none', userSelect: 'none' }}>
          YALLA SALON
        </div>

        <div className="hero-container" style={{ maxWidth: '1280px', margin: '0 auto', padding: '6rem 1.5rem 4rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center', position: 'relative', zIndex: 1, width: '100%' }}>

          {/* Left: text */}
          <div className="hero-text">
            {/* Top label */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <div style={{ width: '30px', height: '2px', background: 'var(--orange)' }} />
              <span style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.25em', textTransform: 'uppercase', color: 'var(--orange)' }}>
                {t('home.heroLabel')}
              </span>
            </div>

            {/* Main headline */}
            <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(4rem, 9vw, 7rem)', letterSpacing: '0.06em', lineHeight: 0.95, marginBottom: '1.5rem', color: '#ffffff' }}>
              YALLA<br />
              <span style={{ color: 'var(--orange)', WebkitTextStroke: '2px var(--orange)', WebkitTextFillColor: 'transparent' }}>SALON</span>
            </h1>

            <p style={{ fontSize: '1rem', color: '#ffffff', lineHeight: 1.8, maxWidth: '420px', marginBottom: '2.5rem' }}>
              {t('home.heroSubtitle')}
            </p>

            {/* CTAs */}
            <div className="hero-ctas" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <Link to={user ? "/shops" : "/login"} className="btn-orange">{t('home.bookAppointment')}</Link>
              <Link to="/products" className="btn-outline-orange">{t('home.buyProducts')}</Link>
            </div>


          </div>

          {/* Right: 3D Logo */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <div className="hero-logo-container" style={{ position: 'relative', width: '420px', height: '420px', maxWidth: '100%', aspectRatio: '1/1' }}>
              {/* 3D Canvas (lazy-loaded to keep initial bundle small) */}
              <Suspense fallback={
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                </div>
              }>
                <Logo3D
                  url="/logo.stl"
                  width="100%"
                  height="100%"
                  color="#ffffff"
                  emissive="#e0d0c0"
                  roughness={0.2}
                  metalness={0.6}
                  enableOrbit={true}
                  accentColor={accentColor}
                  accentLight={accentLight}
                  accentDark={accentDark}
                  sparkleSecondary={sparkleSecondary}
                />
              </Suspense>
              {/* Orange corner accents */}
              <div style={{ position: 'absolute', top: '-2px', left: '-2px', width: '30px', height: '30px', borderTop: '3px solid var(--orange)', borderLeft: '3px solid var(--orange)', borderRadius: '4px 0 0 0', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', top: '-2px', right: '-2px', width: '30px', height: '30px', borderTop: '3px solid var(--orange)', borderRight: '3px solid var(--orange)', borderRadius: '0 4px 0 0', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', bottom: '-2px', left: '-2px', width: '30px', height: '30px', borderBottom: '3px solid var(--orange)', borderLeft: '3px solid var(--orange)', borderRadius: '0 0 0 4px', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', bottom: '-2px', right: '-2px', width: '30px', height: '30px', borderBottom: '3px solid var(--orange)', borderRight: '3px solid var(--orange)', borderRadius: '0 0 4px 0', pointerEvents: 'none' }} />
              {/* Hint label */}
              <div style={{ position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)', fontFamily: "'Oswald', sans-serif", fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#ffffff', pointerEvents: 'none', whiteSpace: 'nowrap' }}>{t('home.dragRotateZoom')}</div>
            </div>
          </div>
        </div>
      </section>





      {/* ════════════════════════════════════════════════════════════════════
          TOP RATED SHOPS (live data)
      ════════════════════════════════════════════════════════════════════ */}
      {displayTopShops.length > 0 && (
        <section style={sectionStyle(false)}>
          <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
            <SectionHeader label={t('home.topRated')} title={t('home.featuredSalons')} titleColor="#ffffff" />
            {loading ? (
              <Skeleton count={3} height="120px" borderRadius="10px" />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.25rem' }}>
                {displayTopShops.map((item) => (
                  <Link
                    key={item.id}
                    to={`/branches?shop_id=${item.id}`}
                    className="barber-card"
                    style={{
                      display: 'block', textDecoration: 'none', color: '#ffffff',
                      borderRadius: '12px', overflow: 'hidden', minHeight: '300px',
                      position: 'relative',
                      background: item.logo_url
                        ? `linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0.9) 100%), url(${resolveMediaUrl(item.logo_url)}) center/cover`
                        : 'linear-gradient(135deg, var(--bg-card) 0%, var(--bg-dark) 100%)',
                    }}
                  >
                    <div style={{ position: 'absolute', top: '1rem', left: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', zIndex: 1 }}>
                      <span style={{ padding: '0.25rem 0.6rem', borderRadius: '6px', background: 'var(--orange)', color: '#ffffff', fontSize: '0.7rem', fontWeight: 700, fontFamily: "'Oswald', sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        {item.category}
                      </span>
                      <span style={{ padding: '0.25rem 0.6rem', borderRadius: '6px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(6px)', color: '#ffffff', fontSize: '0.75rem', fontWeight: 700 }}>
                        {formatRating(item.rating)}
                      </span>
                    </div>
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '1.5rem 1.25rem', zIndex: 1 }}>
                      <h3 style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1.4rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.5rem', color: '#ffffff', lineHeight: 1.2 }}>{item.name}</h3>
                      {item.locations && item.locations.length > 0 && (
                        <p style={{ fontSize: '0.82rem', color: '#ffffff', opacity: 0.95, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <span>📍</span> {item.locations[0]}
                        </p>
                      )}
                      <div
                        className="branch-card-book-btn"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.6rem',
                          width: '100%',
                          marginTop: '1rem',
                          padding: '0.85rem 1.25rem',
                          borderRadius: '9999px',
                          background: 'var(--orange)',
                          color: '#ffffff',
                          fontFamily: "'Oswald', sans-serif",
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          transition: 'box-shadow 0.2s',
                        }}
                      >
                        {t('shops.viewShop')}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      )}



      {/* ════════════════════════════════════════════════════════════════════
          TOP ARTISTS (live data)
      ════════════════════════════════════════════════════════════════════ */}
      {displayTopArtists.length > 0 && (
        <>
          <div className="orange-bar" />
          <section style={sectionStyle(true)}>
            <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
              <SectionHeader label={t('home.ourProfessionals')} title={t('home.topArtists')} titleColor="#ffffff" />
              {loading ? (
                <Skeleton count={3} height="120px" borderRadius="10px" />
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1.25rem' }}>
                  {displayTopArtists.map(artist => (
                    <div key={artist.id} className="barber-card" style={{ padding: '1.5rem', textAlign: 'center' }}>
                      <div style={{ width: '60px', height: '60px', borderRadius: '50%', margin: '0 auto 1rem', overflow: 'hidden', background: 'linear-gradient(135deg, var(--orange), var(--orange-dark))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {artist.photo_url ? (
                          <img src={resolveMediaUrl(artist.photo_url)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.5rem', color: '#fff' }}>{artist.name?.[0] || '?'}</span>
                        )}
                      </div>
                      <h3 style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '0.3rem' }}>{artist.name}</h3>
                      <p style={{ fontSize: '0.8rem', color: '#ffffff', marginBottom: '0.3rem' }}>{artist.role}</p>
                      {artist.branch_name && <p style={{ fontSize: '0.75rem', color: '#ffffff' }}>{artist.branch_name}{artist.shop_name ? ` · ${artist.shop_name}` : ''}</p>}
                      <p style={{ fontSize: '0.85rem', fontWeight: 700, color: '#ffffff', marginTop: '0.5rem' }}>{formatRating(artist.rating)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Section transition: wave from TOP ARTISTS into CTA */}
          <div style={{ position: 'relative', width: '100%', height: '72px', marginTop: '-1px', overflow: 'hidden' }} aria-hidden="true">
            <svg viewBox="0 0 1440 72" preserveAspectRatio="none" style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '100%', display: 'block' }}>
              <path
                fill="var(--bg-dark)"
                d="M0,36 C360,72 720,0 1080,36 C1260,54 1380,18 1440,36 L1440,72 L0,72 Z"
              />
              <path
                fill="var(--bg-base)"
                opacity="0.6"
                d="M0,48 C240,12 480,60 720,36 C960,12 1200,48 1440,24 L1440,72 L0,72 Z"
              />
            </svg>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '2px', background: 'linear-gradient(90deg, transparent, var(--orange), transparent)', opacity: 0.5 }} />
          </div>
        </>
      )}


    </div>
  );
}
